import { data_from_server_post } from '@/shared/http/client';
import { TOOL_SOURCE_TYPES, effectiveExecutionUrl, isBlankToolSource } from './agentConfigModel';

export const EXECUTION_VALIDATION_ENDPOINT = 'agent-tools/validate-execution';

export const EXECUTION_VALIDATION_REQUEST_FAILED = 'Could not validate the tool execution URL';
export const EXECUTION_URL_INVALID = 'Tool execution URL is not valid';

/** The card field that holds the execution URL (MCP may reuse the discovery link). */
export const executionUrlField = (source) =>
    source.type === TOOL_SOURCE_TYPES.mcp && source.use_same_mcp_for_execution ? 'discovery_url' : 'execution_url';

/**
 * The checked tools of a source as `{name, input_schema}`; the schema (when
 * the source has one) lets the backend compare an MCP server's signature.
 */
export const selectedToolSignatures = (source) => {
    const byName = new Map((source.tools || []).map((tool) => [tool.name, tool]));
    return (source.selected_tools || []).map((name) => ({
        name,
        input_schema: byName.get(name)?.input_schema ?? null,
    }));
};

/** `POST agent-tools/validate-execution` body: one entry per non-blank source. */
export const executionValidationRequest = (sources) => ({
    sources: (sources || [])
        .filter((source) => !isBlankToolSource(source))
        .map((source) => ({
            id: source.id,
            type: source.type,
            url: effectiveExecutionUrl(source),
            selected_tools: selectedToolSignatures(source),
        })),
});

/**
 * Probes every card's execution URL on the backend without invoking a tool
 * and resolves with `{ ok, failures: [{ id, error }] }`.
 *
 * An MCP URL fails when it is unreachable or does not list every selected
 * tool with the same signature; any other URL fails only when it is
 * unreachable or answers 5xx (nothing is reported otherwise). A failure of
 * the request itself is one failure with `id: null`.
 */
export const validateExecutionUrls = async (sources) => {
    let response = null;
    let failure = null;
    await data_from_server_post(
        EXECUTION_VALIDATION_ENDPOINT,
        executionValidationRequest(sources),
        (result) => {
            response = result;
        },
        (err) => {
            failure = err;
        }
    );
    if (failure || !response) {
        const error =
            (typeof failure === 'string' && failure) || failure?.message || EXECUTION_VALIDATION_REQUEST_FAILED;
        return { ok: false, failures: [{ id: null, error }] };
    }
    const results = Array.isArray(response.results) ? response.results : [];
    return {
        ok: Boolean(response.success),
        failures: results
            .filter((result) => !result.success)
            .map((result) => ({ id: result.id, error: result.error || EXECUTION_URL_INVALID })),
    };
};

export default validateExecutionUrls;
